import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  Code,
  Eye,
  Flag,
  Languages,
  MessageSquare,
  RefreshCw,
  Save,
  Search,
  Send,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type {
  ApprovalStatus,
  ContentDepth,
  ContentForReview,
  ContentNature,
  ContentOriginality,
  ContentTheme,
  ContentTimeliness,
  PeriodFilter,
  ReviewStatus,
  SubjectEntity,
  Tag,
} from '@/api/services';
import {
  flattenInfinitePages,
  hasHumanRevision,
  useAdminComments,
  useApproveContent,
  useContentForReview,
  useCreateAdminComment,
  useDeleteAdminComment,
  useInfiniteContentList,
  useNewsletterSendPreview,
  useRejectContent,
  useTags,
  useToggleFlag,
  useTranslateContent,
  useUpdateContent,
} from '@/features/admin/hooks/useAdminReview';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { cn } from '@/lib/utils';
import { useAuth } from '@/providers/AuthProvider';

// --- Components ---

function StatusDot({
  status,
  flagged,
  t,
}: {
  status: ApprovalStatus;
  flagged: boolean;
  t: (key: string) => string;
}) {
  // If flagged, show a different indicator
  if (flagged) {
    return (
      <div className="w-2.5 h-2.5 rounded-full bg-[#6B5B95]" title={t('admin.status.flagged')} />
    );
  }

  const statusConfig = {
    APPROVED: { color: 'bg-[#5C8A6E]', titleKey: 'admin.status.approved' },
    PENDING: { color: 'bg-[#D4A35A]', titleKey: 'admin.status.pending' },
    REJECTED: { color: 'bg-[#C45A5A]', titleKey: 'admin.status.rejected' },
  };

  const config = statusConfig[status] || statusConfig.PENDING;

  return (
    <div className={cn('w-2.5 h-2.5 rounded-full', config.color)} title={t(config.titleKey)} />
  );
}

function RevisionBadge({ source }: { source: 'AI' | 'HUMAN' }) {
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider',
        source === 'AI' ? 'bg-[#E8E4F3] text-[#6B5B95]' : 'bg-[#E1F0E8] text-[#5C8A6E]',
      )}
    >
      {source}
    </span>
  );
}

function ConfidenceBadge({ value }: { value?: number }) {
  if (value === undefined) return null;
  const percentage = Math.round(value * 100);
  const color =
    percentage >= 80 ? 'text-[#5C8A6E]' : percentage >= 50 ? 'text-[#D4A35A]' : 'text-[#C45A5A]';
  return <span className={cn('text-[10px] font-medium', color)}>{percentage}%</span>;
}

// Classification options
const THEME_OPTIONS: ContentTheme[] = [
  'AI',
  'Engineering',
  'Product',
  'Design',
  'Startups',
  'Business',
  'Tools',
];
const NATURE_OPTIONS: ContentNature[] = [
  'News',
  'Opinion',
  'Tutorial',
  'Analysis',
  'Announcement',
  'Curation',
];
const DEPTH_OPTIONS: ContentDepth[] = ['Shallow', 'Moderate', 'Deep'];
const TIMELINESS_OPTIONS: ContentTimeliness[] = ['Breaking', 'Time-sensitive', 'Evergreen'];
const ORIGINALITY_OPTIONS: ContentOriginality[] = [
  'Primary source',
  'Commentary',
  'Repost',
  'Curation',
];

function ClassificationField({
  label,
  value,
  suggested,
  confidence,
  options,
  isEditing,
  onChange,
  t,
}: {
  label: string;
  value: string | null;
  suggested?: string | null;
  confidence?: number;
  options?: string[];
  isEditing?: boolean;
  onChange?: (value: string) => void;
  t: (key: string) => string;
}) {
  const displayValue = value || suggested;

  return (
    <div className="p-3 bg-[#F4F2F2] rounded border border-[#CAC7C7]">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-[#888185] uppercase tracking-wider">{label}</span>
        <ConfidenceBadge value={confidence} />
      </div>
      {isEditing && options && onChange ? (
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full p-2 text-sm font-medium text-[#2E2A23] bg-white border border-[#E94E1B] rounded focus:outline-none focus:ring-1 focus:ring-[#E94E1B] cursor-pointer"
        >
          <option value="">{t('admin.fields.notClassified')}</option>
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      ) : (
        <div className="flex items-center gap-2">
          <span className={cn('text-sm font-medium', value ? 'text-[#2E2A23]' : 'text-[#6B5B95]')}>
            {displayValue || t('admin.fields.notClassified')}
          </span>
          {!value && suggested && (
            <span className="text-[10px] text-[#6B5B95] bg-[#E8E4F3] px-1.5 py-0.5 rounded">
              {t('admin.fields.suggested')}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function QualityScoreField({
  label,
  value,
  description,
}: {
  label: string;
  value: number | null;
  description: string;
}) {
  const percentage = value !== null ? Math.round(value * 100) : null;
  const barColor =
    percentage !== null
      ? percentage >= 70
        ? 'bg-[#5C8A6E]'
        : percentage >= 40
          ? 'bg-[#D4A35A]'
          : 'bg-[#C45A5A]'
      : 'bg-[#CAC7C7]';

  return (
    <div className="p-3 bg-[#F4F2F2] rounded border border-[#CAC7C7]">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-[#888185] uppercase tracking-wider">{label}</span>
        <span className="text-sm font-bold text-[#2E2A23]">
          {percentage !== null ? `${percentage}%` : 'N/A'}
        </span>
      </div>
      <div className="w-full h-2 bg-[#CAC7C7] rounded-full overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all', barColor)}
          style={{ width: `${percentage ?? 0}%` }}
        />
      </div>
      <p className="text-[10px] text-[#888185] mt-1">{description}</p>
    </div>
  );
}

function SubjectBadge({
  subject,
  isPrimary = false,
}: {
  subject: SubjectEntity;
  isPrimary?: boolean;
}) {
  const typeColors: Record<string, string> = {
    person: 'bg-[#E8E4F3] text-[#6B5B95]',
    company: 'bg-[#E1F0E8] text-[#5C8A6E]',
    product: 'bg-[#FDEAE3] text-[#E94E1B]',
    organization: 'bg-[#FEF3E2] text-[#D4A35A]',
    technology: 'bg-[#CAC7C7] text-[#888185]',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-1 rounded text-xs',
        isPrimary ? 'font-medium border-2' : '',
        typeColors[subject.type] || typeColors.technology,
      )}
    >
      {subject.name}
      <span className="text-[10px] opacity-60 uppercase">{subject.type}</span>
    </span>
  );
}

function FieldComparison({
  label,
  aiValue,
  currentValue,
  isEditing,
  onEdit,
  multiline = false,
  t,
}: {
  label: string;
  aiValue: string | undefined;
  currentValue: string | undefined;
  isEditing: boolean;
  onEdit: (value: string) => void;
  multiline?: boolean;
  t: (key: string) => string;
}) {
  const hasChanged = aiValue !== currentValue;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-[#888185] uppercase tracking-wider">{label}</span>
        {hasChanged && (
          <span className="text-[10px] text-[#D4A35A] font-medium uppercase">
            {t('admin.fields.modified')}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* AI Value (read-only) */}
        <div className="flex flex-col">
          <div className="flex items-center gap-2 mb-1">
            <RevisionBadge source="AI" />
            <span className="text-[10px] text-[#888185]">{t('admin.fields.generated')}</span>
          </div>
          <div
            className={cn(
              'p-3 rounded border text-sm flex-1',
              'bg-[#F4F2F2] border-[#CAC7C7] text-[#888185]',
              multiline && 'min-h-[100px]',
            )}
          >
            {aiValue || (
              <span className="italic text-[#888185]">{t('admin.fields.notGenerated')}</span>
            )}
          </div>
        </div>

        {/* Current Value (editable) */}
        <div className="flex flex-col">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] text-[#2E2A23] font-medium uppercase tracking-wider">
              {t('admin.fields.current')}
            </span>
            {hasChanged && <RevisionBadge source="HUMAN" />}
          </div>
          {isEditing ? (
            multiline ? (
              <textarea
                value={currentValue || ''}
                onChange={(e) => onEdit(e.target.value)}
                className={cn(
                  'w-full p-3 rounded border text-sm transition-all resize-none flex-1 min-h-[100px]',
                  'bg-white border-[#E94E1B] ring-1 ring-[#E94E1B] text-[#2E2A23]',
                )}
              />
            ) : (
              <input
                type="text"
                value={currentValue || ''}
                onChange={(e) => onEdit(e.target.value)}
                className={cn(
                  'w-full p-3 rounded border text-sm transition-all',
                  'bg-white border-[#E94E1B] ring-1 ring-[#E94E1B] text-[#2E2A23]',
                )}
              />
            )
          ) : (
            <div
              className={cn(
                'p-3 rounded border text-sm flex-1',
                'bg-[#F4F2F2] border-[#CAC7C7] text-[#2E2A23]',
                multiline && 'min-h-[100px]',
              )}
            >
              {currentValue || (
                <span className="italic text-[#888185]">{t('admin.fields.notGenerated')}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TagSelector({
  availableTags,
  selectedTagIds,
  aiTagIds,
  isEditing,
  onChange,
  t,
}: {
  availableTags: Tag[];
  selectedTagIds: string[];
  aiTagIds: string[];
  isEditing: boolean;
  onChange: (tagIds: string[]) => void;
  t: (key: string) => string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const groupedTags = useMemo(() => {
    const groups: Record<string, Tag[]> = {};
    availableTags.forEach((tag) => {
      const category = tag.category || 'Other';
      if (!groups[category]) groups[category] = [];
      groups[category].push(tag);
    });
    return groups;
  }, [availableTags]);

  const toggleTag = (tagId: string) => {
    if (selectedTagIds.includes(tagId)) {
      onChange(selectedTagIds.filter((id) => id !== tagId));
    } else {
      onChange([...selectedTagIds, tagId]);
    }
  };

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-[#888185] uppercase tracking-wider">
          {t('admin.fields.tags')}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* AI Tags */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <RevisionBadge source="AI" />
            <span className="text-[10px] text-[#888185]">{t('admin.fields.generated')}</span>
          </div>
          <div className="p-3 rounded border bg-[#F4F2F2] border-[#CAC7C7] min-h-[80px]">
            <div className="flex flex-wrap gap-2">
              {aiTagIds.length > 0 ? (
                aiTagIds.map((tagId) => {
                  const tag = availableTags.find((t) => t.id === tagId);
                  return (
                    <span
                      key={tagId}
                      className="inline-flex items-center px-2 py-1 rounded text-xs bg-[#F4F2F2] text-[#888185]"
                    >
                      {tag?.name || tagId}
                    </span>
                  );
                })
              ) : (
                <span className="italic text-[#888185] text-sm">
                  {t('admin.fields.noTagsGenerated')}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Current Tags */}
        <div className="relative" ref={dropdownRef}>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] text-[#2E2A23] font-medium uppercase tracking-wider">
              {t('admin.fields.current')}
            </span>
          </div>
          <button
            type="button"
            onClick={() => isEditing && setIsOpen(!isOpen)}
            className={cn(
              'p-3 rounded border min-h-[80px] transition-all w-full text-left',
              isEditing
                ? 'bg-white border-[#CAC7C7] hover:border-[#E94E1B] cursor-pointer'
                : 'bg-[#F4F2F2] border-[#CAC7C7]',
            )}
          >
            <div className="flex flex-wrap gap-2">
              {selectedTagIds.length > 0 ? (
                selectedTagIds.map((tagId) => {
                  const tag = availableTags.find((t) => t.id === tagId);
                  return (
                    <span
                      key={tagId}
                      className={cn(
                        'inline-flex items-center px-2 py-1 rounded text-xs',
                        isEditing
                          ? 'bg-[#FDEAE3] text-[#E94E1B] border border-[#E94E1B]/30'
                          : 'bg-white border border-[#CAC7C7] text-[#2E2A23]',
                      )}
                    >
                      {tag?.name || tagId}
                      {isEditing && (
                        // biome-ignore lint/a11y/useSemanticElements: nested inside <button>, cannot use <button> here
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleTag(tagId);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleTag(tagId);
                            }
                          }}
                          className="ml-1 hover:text-[#C45A5A] cursor-pointer"
                        >
                          <X className="w-3 h-3" />
                        </span>
                      )}
                    </span>
                  );
                })
              ) : (
                <span className="italic text-[#888185] text-sm">
                  {t('admin.fields.noTagsSelected')}
                </span>
              )}
            </div>
          </button>

          {/* Tag Dropdown */}
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                className="absolute z-20 w-full mt-1 bg-white border border-[#CAC7C7] rounded-md shadow-lg max-h-60 overflow-auto"
              >
                {Object.entries(groupedTags).map(([category, tags]) => (
                  <div key={category}>
                    <div className="px-3 py-2 text-[10px] font-bold text-[#888185] uppercase tracking-wider bg-[#F4F2F2]">
                      {category}
                    </div>
                    {tags.map((tag) => (
                      <button
                        type="button"
                        key={tag.id}
                        onClick={() => toggleTag(tag.id)}
                        className={cn(
                          'w-full text-left px-3 py-2 text-sm hover:bg-[#F4F2F2] flex items-center justify-between cursor-pointer',
                          selectedTagIds.includes(tag.id)
                            ? 'text-[#E94E1B] bg-[#FDEAE3]'
                            : 'text-[#2E2A23]',
                        )}
                      >
                        {tag.name}
                        {selectedTagIds.includes(tag.id) && <Check className="w-4 h-4" />}
                      </button>
                    ))}
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

/** Join content sections with line break using the separator from the API */
function processContentBody(
  body: string | null | undefined,
  separator: string | null | undefined,
): string {
  if (!body) return '';
  if (!separator) return body;
  return body.split(separator).join('<br/>');
}

function ContentViewer({
  content,
  separator,
  t,
}: {
  content: string | null | undefined;
  separator: string | null | undefined;
  t: (key: string) => string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [viewMode, setViewMode] = useState<'rendered' | 'html'>('rendered');

  return (
    <div className="mt-8 pt-6 border-t border-[#CAC7C7]">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between mb-4 cursor-pointer"
      >
        <h4 className="text-xs font-bold text-[#888185] uppercase tracking-widest">
          {t('admin.content.title')}
        </h4>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-[#888185]" />
        ) : (
          <ChevronDown className="w-4 h-4 text-[#888185]" />
        )}
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            {/* View mode toggle */}
            <div className="flex items-center gap-2 mb-4">
              <button
                type="button"
                onClick={() => setViewMode('rendered')}
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded transition-colors cursor-pointer',
                  viewMode === 'rendered'
                    ? 'bg-[#2E2A23] text-white'
                    : 'bg-white border border-[#CAC7C7] text-[#888185] hover:border-[#888185]',
                )}
              >
                <Eye className="w-4 h-4" />
                {t('admin.content.rendered')}
              </button>
              <button
                type="button"
                onClick={() => setViewMode('html')}
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded transition-colors cursor-pointer',
                  viewMode === 'html'
                    ? 'bg-[#2E2A23] text-white'
                    : 'bg-white border border-[#CAC7C7] text-[#888185] hover:border-[#888185]',
                )}
              >
                <Code className="w-4 h-4" />
                {t('admin.content.html')}
              </button>
            </div>

            {/* Content display */}
            <div
              className={cn(
                'rounded border border-[#CAC7C7] overflow-auto max-h-[600px]',
                viewMode === 'html' ? 'bg-[#2E2A23] p-4' : 'bg-white p-6',
              )}
            >
              {viewMode === 'rendered' ? (
                <div
                  className="prose prose-sm max-w-none text-[#2E2A23]"
                  dangerouslySetInnerHTML={{ __html: processContentBody(content, separator) }}
                />
              ) : (
                <pre className="text-xs text-[#CAC7C7] whitespace-pre-wrap font-mono">
                  {content}
                </pre>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function AdminCommentsSection({ contentId, t }: { contentId: string; t: (key: string) => string }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [newComment, setNewComment] = useState('');
  const { user } = useAuth();

  const commentsQuery = useAdminComments(isExpanded ? contentId : null);
  const createMutation = useCreateAdminComment();
  const deleteMutation = useDeleteAdminComment();

  const comments = commentsQuery.data ?? [];

  const handleSubmit = () => {
    if (!newComment.trim()) return;
    createMutation.mutate(
      { contentId, content: newComment },
      { onSuccess: () => setNewComment('') },
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="mt-8 pt-6 border-t border-[#CAC7C7]">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between mb-4 cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-[#888185]" />
          <h4 className="text-xs font-bold text-[#888185] uppercase tracking-widest">
            {t('admin.comments.title')}
          </h4>
          {comments.length > 0 && (
            <span className="text-xs text-[#888185]">({comments.length})</span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-[#888185]" />
        ) : (
          <ChevronDown className="w-4 h-4 text-[#888185]" />
        )}
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            {/* Comment input */}
            <div className="mb-4">
              <div className="flex gap-2">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('admin.comments.placeholder')}
                  maxLength={200}
                  rows={2}
                  className="flex-1 p-3 text-sm bg-white border border-[#CAC7C7] rounded resize-none focus:outline-none focus:border-[#E94E1B] focus:ring-1 focus:ring-[#E94E1B]"
                />
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!newComment.trim() || createMutation.isPending}
                  className={cn(
                    'px-4 py-2 rounded text-sm font-medium transition-colors cursor-pointer self-end',
                    newComment.trim()
                      ? 'bg-[#E94E1B] text-white hover:bg-[#C43D0F]'
                      : 'bg-[#CAC7C7] text-[#888185] cursor-not-allowed',
                  )}
                >
                  {createMutation.isPending ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
              {newComment.length > 0 && (
                <div className="text-right text-xs text-[#888185] mt-1">
                  {newComment.length}/200
                </div>
              )}
            </div>

            {/* Comments list */}
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {commentsQuery.isLoading ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#E94E1B]" />
                </div>
              ) : comments.length === 0 ? (
                <p className="text-sm text-[#888185] italic py-4 text-center">
                  {t('admin.comments.noComments')}
                </p>
              ) : (
                <AnimatePresence>
                  {comments.map((comment) => (
                    <motion.div
                      key={comment.id}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="p-3 bg-[#F4F2F2] rounded border border-[#CAC7C7]"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-[#2E2A23]">
                              {comment.user_name}
                            </span>
                          </div>
                          <p className="text-sm text-[#2E2A23]">{comment.content}</p>
                        </div>
                        {user?.id === comment.user_id && (
                          <button
                            type="button"
                            onClick={() =>
                              deleteMutation.mutate({ commentId: comment.id, contentId })
                            }
                            disabled={deleteMutation.isPending}
                            className="p-1 text-[#888185] hover:text-[#C45A5A] transition-colors cursor-pointer"
                            title={t('common.delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function NewsletterPreviewPanel({
  sendId,
  onClose,
  onSelectContent,
  t,
}: {
  sendId: string;
  onClose: () => void;
  onSelectContent: (id: string) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const query = useNewsletterSendPreview(sendId);
  const preview = query.data;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.15 }}
        className="bg-white rounded-lg shadow-xl w-[640px] max-h-[80vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#CAC7C7]">
          <div className="flex items-center gap-3 min-w-0">
            <Send className="w-5 h-5 text-[#E94E1B] flex-none" />
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-[#2E2A23] truncate">
                {preview?.subject ?? '...'}
              </h2>
              {preview && (
                <p className="text-xs text-[#888185] mt-0.5">
                  {t('admin.newsletter.sentAt')} {new Date(preview.sent_at).toLocaleString()} ·{' '}
                  {preview.subscriber_count} {t('admin.newsletter.recipients')}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-[#888185] hover:text-[#2E2A23] transition-colors cursor-pointer flex-none"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content list */}
        <div className="flex-1 overflow-y-auto p-6">
          {query.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E94E1B]" />
            </div>
          ) : !preview || preview.items.length === 0 ? (
            <p className="text-sm text-[#888185] italic text-center py-8">
              {t('admin.list.noContent')}
            </p>
          ) : (
            <div className="space-y-3">
              {preview.items.map((item, idx) => (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => {
                    onSelectContent(item.id);
                    onClose();
                  }}
                  className="w-full text-left p-4 bg-[#F4F2F2] rounded border border-[#CAC7C7] hover:bg-[#F4F2F2] hover:border-[#E94E1B]/30 transition-colors cursor-pointer"
                >
                  <div className="flex items-start gap-3">
                    <span className="text-[10px] font-bold text-[#888185] mt-1 min-w-[20px]">
                      #{idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-[#2E2A23] mb-1 leading-snug">
                        {item.title}
                      </h4>
                      {item.summary && (
                        <p className="text-xs text-[#888185] leading-relaxed">{item.summary}</p>
                      )}
                      {item.category && (
                        <span className="text-[10px] font-bold text-[#E94E1B] uppercase tracking-wide mt-1.5 block">
                          {item.category}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E94E1B]" />
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-[#888185]">
      <p className="text-sm">{message}</p>
    </div>
  );
}

export function AdminReviewPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // State
  const [statusFilter, setStatusFilter] = useState<ReviewStatus>('pending');
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('all');
  const [confidenceFilter, setConfidenceFilter] = useState<number>(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [previewNewsletterSendId, setPreviewNewsletterSendId] = useState<string | null>(null);

  // Editable state for the form
  const [editedTitle, setEditedTitle] = useState<string>('');
  const [editedSummary, setEditedSummary] = useState<string>('');
  const [editedCategory, setEditedCategory] = useState<string>('');
  const [editedAuthor, setEditedAuthor] = useState<string>('');
  const [editedTags, setEditedTags] = useState<string[]>([]);
  // Classification fields
  const [editedTheme, setEditedTheme] = useState<ContentTheme | null>(null);
  const [editedNature, setEditedNature] = useState<ContentNature | null>(null);
  const [editedDepth, setEditedDepth] = useState<ContentDepth | null>(null);
  const [editedTimeliness, setEditedTimeliness] = useState<ContentTimeliness | null>(null);
  const [editedOriginality, setEditedOriginality] = useState<ContentOriginality | null>(null);

  // Debounce search query for server-side search
  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  // Ref for infinite scroll container
  const listContainerRef = useRef<HTMLDivElement>(null);

  // Queries - now with server-side search and infinite scroll
  const contentListQuery = useInfiniteContentList({
    status: statusFilter === 'flagged' || statusFilter === 'newsletter' ? 'all' : statusFilter,
    period: periodFilter,
    minConfidence: confidenceFilter > 0 ? confidenceFilter : undefined,
    flagged: statusFilter === 'flagged' ? true : undefined,
    newsletter: statusFilter === 'newsletter' ? true : undefined,
    search: debouncedSearch || undefined,
  });
  const contentQuery = useContentForReview(selectedId);
  const tagsQuery = useTags();
  const updateMutation = useUpdateContent();
  const approveMutation = useApproveContent();
  const rejectMutation = useRejectContent();
  const flagMutation = useToggleFlag();
  const translateMutation = useTranslateContent();

  // Flatten infinite query pages into items array
  const { items, total } = flattenInfinitePages(contentListQuery.data?.pages);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const container = listContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

    if (isNearBottom && contentListQuery.hasNextPage && !contentListQuery.isFetchingNextPage) {
      contentListQuery.fetchNextPage();
    }
  }, [contentListQuery]);

  // Initialize edit state when content loads
  const content = contentQuery.data;

  const initializeEditState = (data: ContentForReview) => {
    setEditedTitle(data.title);
    setEditedSummary(data.summary || '');
    setEditedCategory(data.category || '');
    setEditedAuthor(data.author || '');
    setEditedTags(data.tags.map((t) => t.id));
    // Classification fields
    setEditedTheme(data.theme);
    setEditedNature(data.nature);
    setEditedDepth(data.depth);
    setEditedTimeliness(data.timeliness);
    setEditedOriginality(data.originality);
  };

  // Effect to initialize edit state when content loads
  if (content && editedTitle === '' && !isEditing) {
    initializeEditState(content);
  }

  const handleSelectContent = (id: string) => {
    setSelectedId(id);
    setIsEditing(false);
    // Reset edit state
    setEditedTitle('');
    setEditedSummary('');
    setEditedCategory('');
    setEditedAuthor('');
    setEditedTags([]);
    // Reset classification state
    setEditedTheme(null);
    setEditedNature(null);
    setEditedDepth(null);
    setEditedTimeliness(null);
    setEditedOriginality(null);
  };

  const handleStartEditing = () => {
    if (content) {
      initializeEditState(content);
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    if (!content) return;

    updateMutation.mutate(
      {
        id: content.id,
        updates: {
          title: editedTitle,
          summary: editedSummary,
          category: editedCategory,
          author: editedAuthor,
          tags: editedTags,
          // Classification fields
          theme: editedTheme || undefined,
          nature: editedNature || undefined,
          depth: editedDepth || undefined,
          timeliness: editedTimeliness || undefined,
          originality: editedOriginality || undefined,
        },
      },
      {
        onSuccess: () => {
          setIsEditing(false);
        },
      },
    );
  };

  const handleResetToAI = () => {
    if (!content) return;
    initializeEditState(content);
  };

  const pendingCount = items.filter((i) => i.approval_status === 'PENDING').length;
  const reviewedCount = items.filter(
    (i) => i.approval_status === 'APPROVED' || i.approval_status === 'REJECTED',
  ).length;
  const flaggedCount = items.filter((i) => i.flagged).length;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col h-screen w-full bg-[#F4F2F2] text-[#2E2A23] font-sans overflow-hidden"
    >
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="h-16 flex-none bg-[#F4F2F2] border-b border-[#CAC7C7] px-6 flex items-center justify-between z-10"
      >
        <div className="flex items-center gap-6">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="flex items-center text-[#888185] hover:text-[#2E2A23] transition-colors text-sm font-medium cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('admin.backToFeed')}
          </button>
          <h1 className="font-serif text-xl font-semibold text-[#2E2A23]">{t('admin.title')}</h1>
        </div>

        <div className="flex items-center gap-4">
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            {(['pending', 'reviewed', 'flagged', 'newsletter', 'all'] as const).map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => setStatusFilter(status)}
                className={cn(
                  'px-3 py-1.5 text-sm font-medium rounded transition-colors cursor-pointer',
                  statusFilter === status
                    ? 'bg-[#2E2A23] text-white'
                    : 'bg-white border border-[#CAC7C7] text-[#888185] hover:border-[#888185]',
                )}
              >
                {t(`admin.filters.status.${status}`)}
              </button>
            ))}
          </div>

          {/* Period Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[#2E2A23]">{t('admin.filters.period')}</span>
            <select
              value={periodFilter}
              onChange={(e) => setPeriodFilter(e.target.value as PeriodFilter)}
              className="px-3 py-1.5 text-sm font-medium rounded bg-white border border-[#CAC7C7] text-[#2E2A23] focus:outline-none focus:border-[#E94E1B] cursor-pointer"
            >
              <option value="all">{t('admin.filters.periodOptions.all')}</option>
              <option value="24h">{t('admin.filters.periodOptions.day')}</option>
              <option value="7d">{t('admin.filters.periodOptions.week')}</option>
              <option value="30d">{t('admin.filters.periodOptions.month')}</option>
            </select>
          </div>

          {/* Confidence Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[#2E2A23]">
              {t('admin.filters.confidence')}
            </span>
            <select
              value={confidenceFilter}
              onChange={(e) => setConfidenceFilter(Number(e.target.value))}
              className="px-3 py-1.5 text-sm font-medium rounded bg-white border border-[#CAC7C7] text-[#2E2A23] focus:outline-none focus:border-[#E94E1B] cursor-pointer"
            >
              <option value={0}>{t('admin.filters.periodOptions.all')}</option>
              <option value={0.5}>≥ 50%</option>
              <option value={0.7}>≥ 70%</option>
              <option value={0.8}>≥ 80%</option>
              <option value={0.9}>≥ 90%</option>
            </select>
          </div>
        </div>
      </motion.header>

      {/* Summary Bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.15 }}
        className="h-10 flex-none bg-[#F4F2F2] border-b border-[#CAC7C7] flex items-center px-6"
      >
        <div className="flex items-center gap-2 text-xs text-[#888185]">
          <span className="font-medium text-[#2E2A23]">{t('admin.summary.total')}</span>
          <span className="w-1 h-1 rounded-full bg-[#888185]" />
          <span>
            <strong className="text-[#2E2A23]">{total}</strong> {t('admin.summary.items')}
          </span>
          <span className="w-1 h-1 rounded-full bg-[#888185]" />
          <span>
            <strong className="text-[#5C8A6E]">{reviewedCount}</strong>{' '}
            {t('admin.summary.reviewed')}
          </span>
          <span className="w-1 h-1 rounded-full bg-[#888185]" />
          <span>
            <strong className="text-[#D4A35A]">{pendingCount}</strong> {t('admin.summary.pending')}
          </span>
          <span className="w-1 h-1 rounded-full bg-[#888185]" />
          <span>
            <strong className="text-[#6B5B95]">{flaggedCount}</strong> {t('admin.summary.flagged')}
          </span>
        </div>
      </motion.div>

      {/* Main Content */}
      <main className="flex-1 flex overflow-hidden">
        {/* Left Panel: Content List */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="w-[360px] flex-none border-r border-[#CAC7C7] bg-[#F4F2F2] flex flex-col"
        >
          <div className="p-4 border-b border-[#CAC7C7] bg-[#F4F2F2]">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-[#888185]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('admin.list.searchPlaceholder')}
                className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-[#CAC7C7] rounded focus:outline-none focus:border-[#E94E1B]"
              />
            </div>
          </div>

          <div ref={listContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto">
            {contentListQuery.isLoading && items.length === 0 ? (
              <LoadingState />
            ) : items.length === 0 ? (
              <EmptyState message={t('admin.list.noContent')} />
            ) : (
              <>
                {items.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() => handleSelectContent(item.id)}
                    className={cn(
                      'w-full text-left p-4 border-b border-[#CAC7C7] hover:bg-[#F4F2F2] transition-colors relative cursor-pointer',
                      selectedId === item.id ? 'bg-[#FDEAE3]' : 'bg-[#F4F2F2]',
                    )}
                  >
                    {selectedId === item.id && (
                      <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#E94E1B]" />
                    )}

                    <div className="flex justify-between items-start mb-1">
                      <div className="flex items-center gap-2">
                        {/* Status bullet first, then score */}
                        <StatusDot status={item.approval_status} flagged={item.flagged} t={t} />
                        {item.avg_confidence !== null && (
                          <span
                            className={cn(
                              'text-[10px] font-bold min-w-[32px] text-center',
                              item.avg_confidence >= 0.8
                                ? 'text-[#5C8A6E]'
                                : item.avg_confidence >= 0.5
                                  ? 'text-[#D4A35A]'
                                  : 'text-[#C45A5A]',
                            )}
                            title={`Confidence: ${Math.round(item.avg_confidence * 100)}%`}
                          >
                            {Math.round(item.avg_confidence * 100)}%
                          </span>
                        )}
                        <span className="text-[11px] font-bold text-[#E94E1B] uppercase tracking-wide">
                          {item.category || 'Uncategorized'}
                        </span>
                      </div>
                      <span className="text-xs text-[#888185]">
                        {item.revision_count} {t('admin.list.revisions')}
                        {item.revision_count !== 1 ? 's' : ''}
                      </span>
                    </div>

                    <h3 className="text-sm font-medium text-[#2E2A23] mb-2 line-clamp-2 leading-snug">
                      {item.title}
                    </h3>

                    <div className="flex justify-between items-center text-xs text-[#888185]">
                      <span>
                        {item.published_at
                          ? new Date(item.published_at).toLocaleDateString()
                          : t('admin.list.noDate')}
                      </span>
                      <div className="flex items-center gap-2">
                        {item.newsletter_send_id && (
                          // biome-ignore lint/a11y/useSemanticElements: nested inside <button>, cannot use <button> here
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewNewsletterSendId(item.newsletter_send_id);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                setPreviewNewsletterSendId(item.newsletter_send_id);
                              }
                            }}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#FDEAE3] text-[#E94E1B] hover:bg-[#FDEAE3] transition-colors cursor-pointer"
                            title={t('admin.newsletter.viewLink')}
                          >
                            <Send className="w-2.5 h-2.5" />
                            NL
                          </span>
                        )}
                        {item.author && (
                          <span
                            className="text-[#888185] truncate max-w-[100px]"
                            title={item.author}
                          >
                            {item.author}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
                {/* Loading indicator for infinite scroll */}
                {contentListQuery.isFetchingNextPage && (
                  <div className="p-4 flex justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#E94E1B]" />
                  </div>
                )}
                {/* End of list indicator */}
                {!contentListQuery.hasNextPage && items.length > 0 && (
                  <div className="p-4 text-center text-xs text-[#888185]">
                    {t('admin.list.showingAll', { count: total })}
                  </div>
                )}
              </>
            )}
          </div>
        </motion.div>

        {/* Right Panel: Detail View */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.25 }}
          className="flex-1 flex flex-col bg-white overflow-hidden relative"
        >
          {!selectedId ? (
            <EmptyState message={t('admin.detail.selectContent')} />
          ) : contentQuery.isLoading ? (
            <LoadingState />
          ) : !content ? (
            <EmptyState message={t('admin.detail.contentNotFound')} />
          ) : (
            <>
              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-8 pb-32">
                {/* Score Badge - centered in the right panel */}
                {(() => {
                  const selectedItem = items.find((item) => item.id === selectedId);
                  if (!selectedItem?.avg_confidence) return null;
                  const score = selectedItem.avg_confidence;
                  return (
                    <div className="flex justify-center mb-6">
                      <div
                        className={cn(
                          'inline-flex items-center gap-2 px-4 py-2 rounded-lg border',
                          score >= 0.8
                            ? 'bg-[#E1F0E8] border-[#5C8A6E]/20 text-[#5C8A6E]'
                            : score >= 0.5
                              ? 'bg-[#FEF3E2] border-[#D4A35A]/20 text-[#D4A35A]'
                              : 'bg-[#FEF2F2] border-[#C45A5A]/20 text-[#C45A5A]',
                        )}
                      >
                        <span className="text-[10px] font-medium uppercase tracking-wider opacity-70">
                          Score
                        </span>
                        <span className="text-lg font-bold">{Math.round(score * 100)}%</span>
                      </div>
                    </div>
                  );
                })()}

                <div className="max-w-4xl mx-auto">
                  {/* Header with revision info */}
                  <div className="flex items-center justify-between mb-8 pb-4 border-b border-[#CAC7C7]">
                    <div>
                      <h2 className="text-xl font-serif font-semibold text-[#2E2A23] mb-2">
                        {content.title}
                      </h2>
                      <div className="flex items-center gap-3 text-xs text-[#888185]">
                        <span>ID: {content.id.substring(0, 8)}...</span>
                        <span className="w-1 h-1 rounded-full bg-[#888185]" />
                        <span>
                          {t('admin.detail.language')}: {content.language.toUpperCase()}
                        </span>
                        <span className="w-1 h-1 rounded-full bg-[#888185]" />
                        <span>
                          {t('admin.detail.revisionsCount')}: {content.revisions.length}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {hasHumanRevision(content.revisions) && (
                        <span className="text-xs text-[#5C8A6E] font-medium">
                          {t('admin.detail.humanReviewed')}
                        </span>
                      )}
                      {!isEditing ? (
                        <button
                          type="button"
                          onClick={handleStartEditing}
                          className="px-4 py-2 text-sm font-medium text-[#E94E1B] border border-[#E94E1B] rounded hover:bg-[#FDEAE3] transition-colors cursor-pointer"
                        >
                          {t('admin.detail.edit')}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={handleResetToAI}
                          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#888185] border border-[#CAC7C7] rounded hover:bg-[#F4F2F2] transition-colors cursor-pointer"
                        >
                          <RefreshCw className="w-4 h-4" />
                          {t('admin.detail.resetToAI')}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Field Comparisons */}
                  <FieldComparison
                    label={t('admin.fields.title')}
                    aiValue={content.title}
                    currentValue={isEditing ? editedTitle : content.title}
                    isEditing={isEditing}
                    onEdit={setEditedTitle}
                    t={t}
                  />

                  <FieldComparison
                    label={t('admin.fields.summary')}
                    aiValue={content.summary || undefined}
                    currentValue={isEditing ? editedSummary : content.summary || undefined}
                    isEditing={isEditing}
                    onEdit={setEditedSummary}
                    multiline
                    t={t}
                  />

                  <FieldComparison
                    label={t('admin.fields.category')}
                    aiValue={content.category || undefined}
                    currentValue={isEditing ? editedCategory : content.category || undefined}
                    isEditing={isEditing}
                    onEdit={setEditedCategory}
                    t={t}
                  />

                  <FieldComparison
                    label={t('admin.fields.author')}
                    aiValue={content.author || undefined}
                    currentValue={isEditing ? editedAuthor : content.author || undefined}
                    isEditing={isEditing}
                    onEdit={setEditedAuthor}
                    t={t}
                  />

                  <TagSelector
                    availableTags={tagsQuery.data ?? []}
                    selectedTagIds={isEditing ? editedTags : content.tags.map((tag) => tag.id)}
                    aiTagIds={content.tags.map((tag) => tag.id)}
                    isEditing={isEditing}
                    onChange={setEditedTags}
                    t={t}
                  />

                  {/* Classification Section */}
                  <div className="mt-8 pt-6 border-t border-[#CAC7C7]">
                    <h4 className="text-xs font-bold text-[#888185] uppercase tracking-widest mb-4">
                      {t('admin.classification.title')}
                    </h4>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <ClassificationField
                        label={t('admin.classification.theme')}
                        value={isEditing ? editedTheme : content.theme}
                        suggested={content.theme_suggested}
                        confidence={content.classification_confidence?.theme}
                        options={THEME_OPTIONS}
                        isEditing={isEditing}
                        onChange={(v) => setEditedTheme(v as ContentTheme)}
                        t={t}
                      />
                      <ClassificationField
                        label={t('admin.classification.nature')}
                        value={isEditing ? editedNature : content.nature}
                        confidence={content.classification_confidence?.nature}
                        options={NATURE_OPTIONS}
                        isEditing={isEditing}
                        onChange={(v) => setEditedNature(v as ContentNature)}
                        t={t}
                      />
                      <ClassificationField
                        label={t('admin.classification.depth')}
                        value={isEditing ? editedDepth : content.depth}
                        confidence={content.classification_confidence?.depth}
                        options={DEPTH_OPTIONS}
                        isEditing={isEditing}
                        onChange={(v) => setEditedDepth(v as ContentDepth)}
                        t={t}
                      />
                      <ClassificationField
                        label={t('admin.classification.timeliness')}
                        value={isEditing ? editedTimeliness : content.timeliness}
                        confidence={content.classification_confidence?.timeliness}
                        options={TIMELINESS_OPTIONS}
                        isEditing={isEditing}
                        onChange={(v) => setEditedTimeliness(v as ContentTimeliness)}
                        t={t}
                      />
                      <ClassificationField
                        label={t('admin.classification.originality')}
                        value={isEditing ? editedOriginality : content.originality}
                        confidence={content.classification_confidence?.originality}
                        options={ORIGINALITY_OPTIONS}
                        isEditing={isEditing}
                        onChange={(v) => setEditedOriginality(v as ContentOriginality)}
                        t={t}
                      />
                    </div>
                  </div>

                  {/* Quality Signals Section */}
                  <div className="mt-6 pt-6 border-t border-[#CAC7C7]">
                    <h4 className="text-xs font-bold text-[#888185] uppercase tracking-widest mb-4">
                      {t('admin.quality.title')}
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <QualityScoreField
                        label={t('admin.quality.authorityScore')}
                        value={content.authority_score}
                        description={t('admin.quality.authorityDesc')}
                      />
                      <QualityScoreField
                        label={t('admin.quality.densityScore')}
                        value={content.density_score}
                        description={t('admin.quality.densityDesc')}
                      />
                    </div>
                  </div>

                  {/* Subjects Section */}
                  <div className="mt-6 pt-6 border-t border-[#CAC7C7]">
                    <h4 className="text-xs font-bold text-[#888185] uppercase tracking-widest mb-4">
                      {t('admin.subjects.title')}
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-xs text-[#888185] uppercase tracking-wider mb-2 block">
                          {t('admin.subjects.primary')}
                        </span>
                        {content.primary_subject ? (
                          <SubjectBadge subject={content.primary_subject} isPrimary />
                        ) : (
                          <span className="text-sm text-[#888185] italic">
                            {t('admin.subjects.notIdentified')}
                          </span>
                        )}
                      </div>
                      <div>
                        <span className="text-xs text-[#888185] uppercase tracking-wider mb-2 block">
                          {t('admin.subjects.mentions')}
                        </span>
                        {content.mentions && content.mentions.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {content.mentions.map((mention, idx) => (
                              <SubjectBadge key={`${mention.name}-${idx}`} subject={mention} />
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-[#888185] italic">
                            {t('admin.subjects.noneIdentified')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Digest Source Articles */}
                  {content.nature === 'Digest' &&
                    (() => {
                      const selectedItem = items.find((item) => item.id === selectedId);
                      const sendId = selectedItem?.newsletter_send_id;
                      if (!sendId) return null;
                      return (
                        <div className="mt-6 pt-6 border-t border-[#CAC7C7]">
                          <h4 className="text-xs font-bold text-[#888185] uppercase tracking-widest mb-4">
                            {t('admin.newsletter.sourceArticles')}
                          </h4>
                          <button
                            type="button"
                            onClick={() => setPreviewNewsletterSendId(sendId)}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#E94E1B] border border-[#E94E1B] rounded hover:bg-[#FDEAE3] transition-colors cursor-pointer"
                          >
                            <Send className="w-4 h-4" />
                            {t('admin.newsletter.viewLink')}
                          </button>
                        </div>
                      );
                    })()}

                  {/* Revision History */}
                  <div className="mt-8 pt-6 border-t border-[#CAC7C7]">
                    <h4 className="text-xs font-bold text-[#888185] uppercase tracking-widest mb-4">
                      {t('admin.revisionHistory.title')}
                    </h4>
                    <div className="space-y-3">
                      {content.revisions.map((rev, index) => (
                        <div
                          key={`${rev.source}-${rev.at}`}
                          className="flex items-start gap-4 p-3 bg-[#F4F2F2] rounded border border-[#CAC7C7]"
                        >
                          <div className="flex-none mt-0.5">
                            <RevisionBadge source={rev.source} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap gap-1.5 mb-1">
                              {Object.keys(rev.data).length > 0 ? (
                                Object.keys(rev.data).map((field) => (
                                  <span
                                    key={field}
                                    className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-[#F4F2F2] text-[#888185] border border-[#CAC7C7]"
                                  >
                                    {t(`admin.fields.${field}`, { defaultValue: field })}
                                  </span>
                                ))
                              ) : (
                                <span className="text-xs italic text-[#888185]">
                                  {t('admin.revisionHistory.noFields')}
                                </span>
                              )}
                            </div>
                            {rev.data.tags && Array.isArray(rev.data.tags) && (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {(rev.data.tags as string[]).map((tagId) => {
                                  const tag = tagsQuery.data?.find((t) => t.id === tagId);
                                  return (
                                    <span
                                      key={tagId}
                                      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-[#FDEAE3] text-[#E94E1B] border border-[#E94E1B]/20"
                                    >
                                      {tag?.name || tagId.substring(0, 8)}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                            <div className="text-xs text-[#888185] mt-1">
                              {new Date(rev.at).toLocaleString()}
                              {rev.by && ` by ${rev.by.substring(0, 8)}...`}
                            </div>
                          </div>
                          <div className="flex-none text-xs text-[#888185]">#{index + 1}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Article Content */}
                  <ContentViewer
                    content={content.body}
                    separator={content.content_separator}
                    t={t}
                  />

                  {/* Admin Comments */}
                  <AdminCommentsSection contentId={content.id} t={t} />
                </div>
              </div>

              {/* Action Bar (Sticky Bottom) */}
              <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-[#CAC7C7] p-4 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] z-20">
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* Status indicator */}
                    <div className="flex items-center gap-2 text-xs">
                      <StatusDot status={content.approval_status} flagged={content.flagged} t={t} />
                      <span
                        className={cn(
                          'font-medium uppercase tracking-wider',
                          content.approval_status === 'APPROVED'
                            ? 'text-[#5C8A6E]'
                            : content.approval_status === 'REJECTED'
                              ? 'text-[#C45A5A]'
                              : 'text-[#D4A35A]',
                        )}
                      >
                        {content.flagged
                          ? t('admin.status.flagged')
                          : t(`admin.status.${content.approval_status.toLowerCase()}`)}
                      </span>
                    </div>
                    <div className="text-xs text-[#888185] flex items-center gap-2">
                      <Clock className="w-3 h-3" />
                      {(() => {
                        const lastHumanRevision = content.revisions
                          .filter((r) => r.source === 'HUMAN')
                          .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())[0];
                        return lastHumanRevision
                          ? t('admin.status.reviewedAt', {
                              date: new Date(lastHumanRevision.at).toLocaleDateString(),
                            })
                          : t('admin.status.notReviewed');
                      })()}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setIsEditing(false)}
                          className="px-4 py-2 text-sm font-medium text-[#888185] border border-[#CAC7C7] rounded hover:bg-[#F4F2F2] transition-colors cursor-pointer"
                        >
                          {t('common.cancel')}
                        </button>
                        <button
                          type="button"
                          onClick={handleSave}
                          disabled={updateMutation.isPending}
                          className={cn(
                            'flex items-center gap-2 px-6 py-2 rounded shadow-md text-sm font-medium transition-colors cursor-pointer',
                            'bg-[#2E2A23] text-[#F4F2F2] hover:bg-black',
                            updateMutation.isPending && 'opacity-50 cursor-not-allowed',
                          )}
                        >
                          {updateMutation.isPending ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          ) : (
                            <>
                              <Save className="w-4 h-4" />
                              {t('admin.actions.saveChanges')}
                            </>
                          )}
                        </button>
                      </>
                    ) : (
                      <>
                        {/* Translate button — only for non-pt content */}
                        {content.language !== 'pt' && (
                          <button
                            type="button"
                            onClick={() => translateMutation.mutate(content.id)}
                            disabled={translateMutation.isPending}
                            className={cn(
                              'flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors border cursor-pointer',
                              'text-[#E94E1B] border-[#E94E1B]/30 hover:bg-[#FDECE5]',
                              translateMutation.isPending && 'opacity-50 cursor-not-allowed',
                            )}
                            title={t('admin.actions.translateTooltip')}
                          >
                            {translateMutation.isPending ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                            ) : (
                              <>
                                <Languages className="w-4 h-4" />
                                {t('admin.actions.translate')}
                              </>
                            )}
                          </button>
                        )}

                        {/* Flag button */}
                        <button
                          type="button"
                          onClick={() => flagMutation.mutate(content.id)}
                          disabled={flagMutation.isPending}
                          className={cn(
                            'flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors border cursor-pointer',
                            content.flagged
                              ? 'bg-[#6B5B95] text-white border-[#6B5B95] hover:bg-[#5a4a84]'
                              : 'text-[#6B5B95] border-[#6B5B95]/30 hover:bg-[#E8E4F3]',
                            flagMutation.isPending && 'opacity-50 cursor-not-allowed',
                          )}
                          title={
                            content.flagged
                              ? t('admin.actions.unflagTooltip')
                              : t('admin.actions.flagTooltip')
                          }
                        >
                          <Flag className="w-4 h-4" />
                          {content.flagged ? t('admin.actions.unflag') : t('admin.actions.flag')}
                        </button>

                        {/* Reject button */}
                        <button
                          type="button"
                          onClick={() => rejectMutation.mutate(content.id)}
                          disabled={
                            rejectMutation.isPending || content.approval_status === 'REJECTED'
                          }
                          className={cn(
                            'flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-colors border cursor-pointer',
                            content.approval_status === 'REJECTED'
                              ? 'bg-[#C45A5A] text-white border-[#C45A5A]'
                              : 'text-[#C45A5A] border-[#C45A5A]/30 hover:bg-[#FEF2F2]',
                            rejectMutation.isPending && 'opacity-50 cursor-not-allowed',
                          )}
                        >
                          {rejectMutation.isPending ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                          ) : (
                            <>
                              <ThumbsDown className="w-4 h-4" />
                              {t('admin.actions.reject')}
                            </>
                          )}
                        </button>

                        {/* Approve button */}
                        <button
                          type="button"
                          onClick={() => approveMutation.mutate(content.id)}
                          disabled={
                            approveMutation.isPending || content.approval_status === 'APPROVED'
                          }
                          className={cn(
                            'flex items-center gap-2 px-4 py-2 rounded shadow-md text-sm font-medium transition-colors cursor-pointer',
                            content.approval_status === 'APPROVED'
                              ? 'bg-[#5C8A6E] text-white'
                              : 'bg-[#5C8A6E] text-white hover:bg-[#4a735b]',
                            (approveMutation.isPending || content.approval_status === 'APPROVED') &&
                              'opacity-50 cursor-not-allowed',
                          )}
                        >
                          {approveMutation.isPending ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          ) : (
                            <>
                              <ThumbsUp className="w-4 h-4" />
                              {t('admin.actions.approve')}
                            </>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </motion.div>
      </main>

      {/* Newsletter Preview Modal */}
      <AnimatePresence>
        {previewNewsletterSendId && (
          <NewsletterPreviewPanel
            sendId={previewNewsletterSendId}
            onClose={() => setPreviewNewsletterSendId(null)}
            onSelectContent={handleSelectContent}
            t={t}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
